import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";

import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { ILeagueData } from "@/types/league";

const options = (...values: string[]) =>
  values.map((value) => ({ label: value, value }));

const enumColumn = (
  id: string,
  header: string,
  key: string,
  opts: { label: string; value: string }[],
  filterType: "faceted-select" | "select" = "faceted-select",
): ColumnDef<ILeagueData> => ({
  id,
  header,
  accessorKey: `document.${key}`,
  enableSorting: false,
  size: 120,
  meta: { filterType, options: opts },
});

const booleanOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export const useLeagueColumns = () =>
  React.useMemo<ColumnDef<ILeagueData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        id: "logo",
        header: "",
        accessorFn: (row) => row.document.media?.photos?.[0],
        enableSorting: false,
        enableColumnFilter: false,
        size: 56,
        cell: function render({ getValue }: any) {
          const photo = getValue();
          return photo?.url ? (
            <PhotoWithBlurhash
              className="size-8 rounded object-contain"
              url={photo.url}
              blurhash={photo.blurhash ?? ""}
            />
          ) : (
            <div className="size-8 rounded bg-muted" />
          );
        },
      },
      {
        id: "name",
        header: "Name",
        minSize: 200,
        size: 280,
        accessorFn: (row) =>
          row.highlights?.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/leagues/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            />,
          );
        },
        enableSorting: false,
        meta: { filterType: "text" },
      },
      enumColumn(
        "sport",
        "Sport",
        "sport",
        options("Football", "Basketball", "Volleyball"),
      ),
      {
        id: "country",
        header: "Country",
        accessorKey: "document.country",
        enableSorting: false,
        size: 140,
        meta: { filterType: "faceted-select" },
      },
      enumColumn(
        "gender",
        "Gender",
        "gender",
        options("Men", "Women", "Mixed"),
      ),
      enumColumn(
        "ageGroup",
        "Age group",
        "ageGroup",
        options("Senior", "Youth"),
      ),
      {
        ...enumColumn(
          "enabled",
          "Enabled",
          "enabled",
          booleanOptions,
          "select",
        ),
        cell: ({ getValue }: any) => (getValue() ? "Yes" : "No"),
      },
      {
        ...enumColumn(
          "status",
          "Status",
          "status",
          options("Draft", "Complete", "Archived"),
          "select",
        ),
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
      },
      {
        ...enumColumn("hide", "Hidden", "hide", booleanOptions, "select"),
        cell: ({ getValue }: any) =>
          getValue() ? <Badge variant="secondary">Hidden</Badge> : null,
      },
      {
        id: "tier",
        header: "Tier",
        accessorKey: "document.tier",
        enableSorting: false,
        size: 80,
        meta: { filterType: "number" },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [],
  );
